/**
 * Process Manager Page
 * 
 * Admin page for monitoring and managing process manager runs
 * Redesigned to match other management pages with consistent UX
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  DataTable,
  TextInput,
  Select,
  Badge,
  toast
} from '@etherisc/ui-kit';
import {
  Search,
  RefreshCw,
  Eye,
  RotateCcw,
  XCircle,
  Play,
  Pause,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import { PermissionGate } from '../../components/PermissionGate';

// Types for process management
interface ProcessRun {
  id: string;
  processKey: string;
  aggregateId: string;
  quoteNumber?: string; // From backend API
  quoteExists?: boolean; // From backend API
  displayQuoteId?: string; // Computed display value
  correlationId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  currentStep?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
  bag?: Record<string, unknown>;
}

interface ProcessStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  attempt: number;
  startedAt?: string;
  finishedAt?: string;
  errorMessage?: string;
  retryAfterAt?: string;
}

interface ProcessRunDetails extends ProcessRun {
  steps: ProcessStep[];
}

// Table cell props
interface TableCellProps {
  row: {
    original: ProcessRun;
  };
}

interface StepTableCellProps {
  row: {
    original: ProcessStep;
  };
}

export function ProcessManagerPage() {
  const [processes, setProcesses] = useState<ProcessRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processKeyFilter, setProcessKeyFilter] = useState('all');
  const [processDetails, setProcessDetails] = useState<ProcessRunDetails | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Load processes from API
  const loadProcesses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (processKeyFilter !== 'all') params.append('processKey', processKeyFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`/api/processes/search?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Check if the response has the expected structure
      if (!result.runs || !Array.isArray(result.runs)) {
        console.error('Unexpected API response structure:', result);
        throw new Error('Invalid API response structure - expected runs array');
      }
      
      // The backend now includes quote information, so we can use it directly
      const processesWithDisplayNames = result.runs.map((run: ProcessRun & { quoteExists?: boolean }) => ({
        ...run,
        // Use the quote number from backend, or create a fallback display
        displayQuoteId: run.quoteNumber || 
                       (run.quoteExists === false ? `${run.aggregateId.substring(0, 8)} (deleted)` : 
                        run.aggregateId.substring(0, 8))
      }));
      
      setProcesses(processesWithDisplayNames);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load processes';
      setError(errorMessage);
      toast.error('Failed to load processes', { description: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, processKeyFilter, searchQuery]);

  // Load process details
  const loadProcessDetails = useCallback(async (runId: string) => {
    try {
      setDetailsLoading(true);
      
      // Find the process run to get its details
      const processRun = processes.find(p => p.id === runId);
      if (!processRun) {
        throw new Error('Process run not found');
      }
      
      // For quote-payment processes, use the specific endpoint
      if (processRun.processKey === 'quote-payment') {
        const response = await fetch(`/api/processes/quote-payment/${processRun.aggregateId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        // Transform to match our interface
        setProcessDetails({
          ...processRun,
          steps: result.steps || []
        });
      } else {
        // For other process types, just show basic info without steps
        setProcessDetails({
          ...processRun,
          steps: []
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load process details';
      toast.error('Failed to load process details', { description: errorMessage });
    } finally {
      setDetailsLoading(false);
    }
  }, [processes]);

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Handle filter changes
  const handleStatusFilter = useCallback((value: string) => {
    setStatusFilter(value);
  }, []);

  const handleProcessKeyFilter = useCallback((value: string) => {
    setProcessKeyFilter(value);
  }, []);

  // Handle view process details
  const handleViewProcess = useCallback((processId: string) => {
    setShowDetailsModal(true);
    loadProcessDetails(processId);
  }, [loadProcessDetails]);

  // Handle retry process
  const handleRetryProcess = useCallback(async (processId: string) => {
    try {
      const response = await fetch(`/api/processes/runs/${processId}/retry`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      toast.success('Process retry initiated');
      loadProcesses();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to retry process';
      toast.error('Process retry failed', { description: errorMessage });
    }
  }, [loadProcesses]);

  // Handle cancel process
  const handleCancelProcess = useCallback(async (processId: string) => {
    try {
      const response = await fetch(`/api/processes/runs/${processId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      toast.success('Process cancelled');
      loadProcesses();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel process';
      toast.error('Process cancel failed', { description: errorMessage });
    }
  }, [loadProcesses]);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setShowDetailsModal(false);
    setProcessDetails(null);
  }, []);

  // Load initial data
  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'running':
        return <Play className="h-4 w-4 text-blue-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <Pause className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get status color for badges
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  // Table columns configuration
  const columns = [
    {
      accessorKey: 'processKey',
      header: 'Process Type',
      cell: ({ row }: TableCellProps) => (
        <div className="font-medium capitalize">
          {row.original.processKey.replace('-', ' ')}
        </div>
      )
    },
    {
      accessorKey: 'displayQuoteId',
      header: 'Quote ID',
      cell: ({ row }: TableCellProps) => (
        <div className="font-medium">
          {row.original.displayQuoteId || row.original.aggregateId.substring(0, 8)}
        </div>
      )
    },
    {
      accessorKey: 'currentStep',
      header: 'Current Step',
      cell: ({ row }: TableCellProps) => (
        <div className="text-sm">
          {row.original.currentStep || 'Not started'}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: TableCellProps) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(row.original.status)}
          <Badge variant={getStatusColor(row.original.status)}>
            {row.original.status}
          </Badge>
        </div>
      )
    },
    {
      accessorKey: 'createdAt',
      header: 'Started',
      cell: ({ row }: TableCellProps) => (
        <div className="text-sm text-gray-600">
          {formatDate(row.original.createdAt)}
        </div>
      )
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Updated',
      cell: ({ row }: TableCellProps) => (
        <div className="text-sm text-gray-600">
          {formatDate(row.original.updatedAt)}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: TableCellProps) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewProcess(row.original.id)}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.original.status === 'failed' && (
            <PermissionGate permission="quotes:update">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRetryProcess(row.original.id)}
                title="Retry Process"
                className="text-blue-600 hover:text-blue-700"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </PermissionGate>
          )}
          {row.original.status === 'running' && (
            <PermissionGate permission="quotes:update">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCancelProcess(row.original.id)}
                title="Cancel Process"
                className="text-red-600 hover:text-red-700"
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </PermissionGate>
          )}
        </div>
      )
    }
  ];

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-600">
              <XCircle className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Processes</h3>
              <p>{error}</p>
              <Button onClick={loadProcesses} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Process Manager</h1>
          <p className="text-gray-600 mt-1">
            Monitor and manage automated business processes including payment workflows
          </p>
        </div>
        <Button onClick={loadProcesses} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-base-content/50 z-10" />
              <div className="pl-10">
                <TextInput
                  placeholder="Search by quote number or correlation ID..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
            <Select
              label="Status"
              value={statusFilter}
              onValueChange={handleStatusFilter}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'running', label: 'Running' },
                { value: 'completed', label: 'Completed' },
                { value: 'failed', label: 'Failed' },
                { value: 'cancelled', label: 'Cancelled' }
              ]}
            />
            <Select
              label="Process Type"
              value={processKeyFilter}
              onValueChange={handleProcessKeyFilter}
              options={[
                { value: 'all', label: 'All Processes' },
                { value: 'quote-payment', label: 'Quote Payment' },
                { value: 'policy-issuance', label: 'Policy Issuance' },
                { value: 'claim-processing', label: 'Claim Processing' }
              ]}
            />
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  setProcessKeyFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Process Runs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Process Runs ({processes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={processes}
            loading={loading}
            enableSorting={true}
            enableResizing={true}
          />
        </CardContent>
      </Card>

      {/* Process Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Process Details</h3>
              <Button variant="ghost" onClick={handleCloseModal}>
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
          
            {detailsLoading ? (
              <div className="flex items-center justify-center p-8">
                <RefreshCw className="h-8 w-8 animate-spin" />
              </div>
            ) : processDetails ? (
              <div className="space-y-6">
                {/* Process Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Process Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Process Type:</span>
                        <span className="font-medium capitalize">{processDetails.processKey.replace('-', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Quote:</span>
                        <span className="font-medium">{processDetails.displayQuoteId || processDetails.aggregateId.substring(0, 8)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(processDetails.status)}
                          <Badge variant={getStatusColor(processDetails.status)}>
                            {processDetails.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Step:</span>
                        <span className="font-medium">{processDetails.currentStep || 'Not started'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Correlation ID:</span>
                        <span className="font-mono text-sm">{processDetails.correlationId}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Timing Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Started:</span>
                        <span className="font-medium">{formatDate(processDetails.createdAt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Updated:</span>
                        <span className="font-medium">{formatDate(processDetails.updatedAt)}</span>
                      </div>
                      {processDetails.completedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Completed:</span>
                          <span className="font-medium">{formatDate(processDetails.completedAt)}</span>
                        </div>
                      )}
                      {processDetails.errorMessage && (
                        <div className="border-t pt-3">
                          <span className="text-gray-600">Error:</span>
                          <p className="text-red-600 text-sm mt-1">{processDetails.errorMessage}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Process Steps */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Process Steps ({processDetails.steps?.length || 0})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {processDetails.steps && processDetails.steps.length > 0 ? (
                      <DataTable
                        columns={[
                          {
                            accessorKey: 'name',
                            header: 'Step Name',
                            cell: ({ row }: StepTableCellProps) => (
                              <div className="font-medium">
                                {row.original.name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </div>
                            )
                          },
                          {
                            accessorKey: 'status',
                            header: 'Status',
                            cell: ({ row }: StepTableCellProps) => (
                              <div className="flex items-center gap-2">
                                {getStatusIcon(row.original.status)}
                                <Badge variant={getStatusColor(row.original.status)}>
                                  {row.original.status}
                                </Badge>
                              </div>
                            )
                          },
                          {
                            accessorKey: 'attempt',
                            header: 'Attempt',
                            cell: ({ row }: StepTableCellProps) => (
                              <span className="font-medium">{row.original.attempt}</span>
                            )
                          },
                          {
                            accessorKey: 'startedAt',
                            header: 'Started At',
                            cell: ({ row }: StepTableCellProps) => (
                              <span className="text-sm">
                                {row.original.startedAt ? formatDate(row.original.startedAt) : 'N/A'}
                              </span>
                            )
                          },
                          {
                            accessorKey: 'finishedAt',
                            header: 'Finished At',
                            cell: ({ row }: StepTableCellProps) => (
                              <span className="text-sm">
                                {row.original.finishedAt ? formatDate(row.original.finishedAt) : 'N/A'}
                              </span>
                            )
                          },
                          {
                            accessorKey: 'errorMessage',
                            header: 'Error',
                            cell: ({ row }: StepTableCellProps) => (
                              <div className="max-w-xs">
                                {row.original.errorMessage ? (
                                  <span className="text-red-600 text-sm truncate block" title={row.original.errorMessage}>
                                    {row.original.errorMessage}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">None</span>
                                )}
                              </div>
                            )
                          }
                        ]}
                        data={processDetails.steps}
                        pagination={false}
                      />
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No process steps found</p>
                        <p className="text-sm">Steps will appear here as the process executes</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  {processDetails.status === 'failed' && (
                    <PermissionGate permission="quotes:update">
                      <Button
                        onClick={() => handleRetryProcess(processDetails.id)}
                        title="Retry this process from the failed step"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retry Process
                      </Button>
                    </PermissionGate>
                  )}
                  {processDetails.status === 'running' && (
                    <PermissionGate permission="quotes:update">
                      <Button
                        variant="outline"
                        onClick={() => handleCancelProcess(processDetails.id)}
                        title="Cancel this running process"
                        className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel Process
                      </Button>
                    </PermissionGate>
                  )}
                  <Button variant="outline" onClick={handleCloseModal}>
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Process details not found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
